import React from 'react';
import { TREND } from '../../../data/landingTrends';
import StatBand, { STAT_ITEM, STAT_LINK, STAT_NUMERAL, STAT_TINT } from '../../StatBand';
import { Eyebrow, Source, StartFree } from './parts';

// The blocks founder pick E brought (2026-09-29, "E, with the patent tile
// fixed"): the contrast line under the hero, "How it works", the trends band
// and one pointer low on the page. Each takes its words as a prop from
// content/landingHome.js, carries its own data-landing-section, and every link
// a data-cta (ids in content/landingIds.js and its Python twin). Figures come
// from data/landingTrends.js only; each shows its source under it.

function TrendSource({ t, dark = false, cta }) {
    return (
        <Source dark={dark}>
            <a href={t.url} data-cta={cta} target="_blank" rel="noopener noreferrer" className={dark ? STAT_LINK : 'underline underline-offset-2 decoration-[#CFE3D4] hover:decoration-[#1A4D2E]'}>
                {t.source}
            </a>
        </Source>
    );
}

// Words that must not break at their hyphen ("top-two" split across two
// lines on desktop in review).
const KEEP = ['top-two'];
function keepTogether(text) {
    const parts = String(text).split(new RegExp(`(${KEEP.join('|')})`));
    return parts.map((p, i) => (KEEP.includes(p) ? <span key={i} className="whitespace-nowrap">{p}</span> : p));
}

/** The founder's idea in one line, right under the hero's words. */
export function Contrast({ text, className = '' }) {
    if (!text) return null;
    return (
        <p data-testid="landing-contrast" className={`mx-auto max-w-2xl text-base font-semibold leading-snug text-[#2C2C2C] [text-wrap:balance] sm:text-lg ${className}`}>
            {text}
        </p>
    );
}

/** E, H: the dark trends band: a title, the tiles, the honest line. */
export function TrendsBand({ copy, onStart }) {
    if (!copy) return null;
    return (
        <StatBand id="trends" data-landing-section="trends" data-testid="landing-trends-block" aria-labelledby="landing-trends-title" className="scroll-mt-20 px-4 py-10 sm:px-6 lg:px-8 lg:py-20">
            <div className="mx-auto max-w-6xl">
                <div className="mx-auto max-w-3xl text-center">
                    <Eyebrow dark>{copy.eyebrow}</Eyebrow>
                    <h2 id="landing-trends-title" className="[text-wrap:balance] font-serif text-3xl font-bold leading-snug sm:text-4xl">{copy.title}</h2>
                </div>
                <ul role="list" className={`m-0 mt-7 grid list-none grid-cols-1 gap-x-6 gap-y-5 p-0 sm:grid-cols-2 ${copy.keys.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
                    {copy.keys.map((k, i) => {
                        const t = TREND[k];
                        return (
                            <li key={k} data-trend={i === 0 ? 'first' : 'rest'} className={STAT_ITEM}>
                                {/* A qualifier ("More than") sits above the numeral, so every
                                    numeral is one line and the bodies start level. */}
                                <p className={`h-5 text-sm font-semibold ${STAT_TINT}`}>{t.qualifier || ''}</p>
                                <p className={`${STAT_NUMERAL} text-4xl sm:text-5xl`}>{t.lead}</p>
                                <p className={`mt-2 text-sm leading-snug ${STAT_TINT} [text-wrap:pretty] sm:text-base`}>{keepTogether(t.line)}</p>
                                <TrendSource t={t} dark cta="trends_source" />
                            </li>
                        );
                    })}
                </ul>
                <p className="mx-auto mt-6 max-w-3xl text-center text-base font-semibold [text-wrap:balance] sm:mt-8 sm:text-lg">{copy.honest}</p>
                {/* The one Start free between the free read and the Build cards, as
                    the evidence block had (a sign-up button every few screens). */}
                {onStart && <div className="mt-5 flex justify-center sm:mt-6"><StartFree onStart={onStart} cta="trends_start_free" tone="white" size="md" /></div>}
            </div>
        </StatBand>
    );
}

/** E: build, it goes on the record, point it where you are headed. */
export function HowItWorks({ copy }) {
    if (!copy) return null;
    return (
        <section id="how" data-landing-section="how" data-testid="landing-how" className="scroll-mt-20 bg-[#FDFCF7] px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
            <div className="mx-auto max-w-5xl">
                <div className="text-center">
                    {copy.eyebrow && <Eyebrow>{copy.eyebrow}</Eyebrow>}
                    <h2 className="[text-wrap:balance] font-serif text-3xl font-bold text-[#1A4D2E] sm:text-4xl">{copy.title}</h2>
                </div>
                <ol className="m-0 mt-6 grid list-none gap-4 p-0 lg:grid-cols-3">
                    {copy.steps.map((s, i) => (
                        <li key={s.title} className="flex flex-col rounded-2xl border border-[#E0DED8] bg-white p-4 sm:p-5">
                            {/* The number beside the title, the body under both at full
                                width (design review: no text column beside the disc). */}
                            <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1A4D2E] font-serif text-base font-bold text-white" aria-hidden="true">{i + 1}</span>
                                <h3 className="font-serif text-xl font-bold leading-tight text-[#1A4D2E] [text-wrap:balance]">{s.title}</h3>
                            </div>
                            <div className="mt-2 min-w-0">
                                <p className="mt-1 text-base leading-relaxed text-[#4A4A4A] [text-wrap:pretty]">{s.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}
